import React from 'react'

import { connect } from 'react-redux'
import styled from 'styled-components'

import type { Locale } from '@libs/typescript-utils'

import { localeActions } from '@services'

import { Button, List, LIST_ITEM_CSS_VARIABLES, ListItem, Popover, Typography } from '@components/ui'

import { generateMaxHeightBreakPoint, generateMaxWidthBreakPoint, generateMinWidthBreakPoint } from '@constants/theme'

const Wrapper = styled.div`
  display: inline-block;
  ${generateMaxWidthBreakPoint('small')} {
    position: absolute;
    right: 15px;
  }
`

const DesktopWrapper = styled.div`
  ${generateMaxWidthBreakPoint('small')} {
    display: none;
  }
`

const MobileWrapper = styled.div`
  & ul {
    display: flex;
    ${LIST_ITEM_CSS_VARIABLES.listItemHorizontalPadding}: 8px;

    ${generateMaxHeightBreakPoint('medium')} {
      ${LIST_ITEM_CSS_VARIABLES.listItemVerticalPadding}: 8px;
    }
  }

  ${generateMinWidthBreakPoint('small')} {
    display: none;
  }
`

const StyledTypography = styled(Typography).attrs({
  size: '700-lg',
  uppercase: true,
})`
  line-height: 1.5;
`

const StyledButton = styled(Button)`
  height: ${({ theme }) => theme.appBarHeight}px;
  width: 60px;
  min-width: 0;
  border-radius: 0;
  font-weight: 700;
  padding: 8px;
  margin: 0 auto;

  &:before {
    display: none;
  }

  & .Select-arrow {
    margin-left: 3px;
    transition: transform 250ms ease;
  }

  &[aria-expanded='true'] .Select-arrow {
    transform: rotateX(180deg) translateY(3.5px);
  }

  ${generateMaxHeightBreakPoint('medium')} {
    height: ${({ theme }) => theme.appBarHeightMedium}px;
  }
`

interface MenuProps {
  handleChange: (newLang: Locale) => () => void
  lang: Locale
}

function Menu({ handleChange, lang }: MenuProps) {
  return (
    <List disablePadding>
      <ListItem button onClick={handleChange('hu')}>
        <StyledTypography color={lang === 'hu' ? 'orange-50' : 'gray-80'} tag="span">
          HU
        </StyledTypography>
      </ListItem>
      <ListItem button onClick={handleChange('en')}>
        <StyledTypography color={lang === 'en' ? 'orange-50' : 'gray-80'} tag="span">
          EN
        </StyledTypography>
      </ListItem>
    </List>
  )
}

interface LocaleSelectorProps {
  changeLocale: (lang: Locale) => void
  lang: Locale
}

function PureLocaleSelector({ changeLocale, lang }: LocaleSelectorProps) {
  const [isOpen, setOpen] = React.useState(false)

  function onOpenChange(nextOpen: boolean) {
    setOpen(nextOpen)
  }

  function handleChange(newLang: Locale) {
    return function handler() {
      setOpen(false)
      if (lang !== newLang) {
        changeLocale(newLang)
      }
    }
  }

  return (
    <Wrapper>
      <DesktopWrapper>
        <Popover
          open={isOpen}
          onOpenChange={onOpenChange}
          placement="bottom"
          trigger={
            <StyledButton>
              {lang.toUpperCase()} <span className="Select-arrow" />
            </StyledButton>
          }
          withoutPortal
          withoutScrollLock
        >
          <Menu handleChange={handleChange} lang={lang} />
        </Popover>
      </DesktopWrapper>
      <MobileWrapper>
        <Menu handleChange={handleChange} lang={lang} />
      </MobileWrapper>
    </Wrapper>
  )
}

export const LocaleSelector = connect(
  (state: Store) => ({
    lang: state.locale.language,
  }),
  {
    changeLocale: localeActions.changeLocale.request,
  }
)(PureLocaleSelector)

LocaleSelector.displayName = 'LocaleSelector'
